import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { DataGridPro, GridLogicOperator } from "@mui/x-data-grid-pro";
import { LicenseInfo } from "@mui/x-license-pro";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import Pagination from "./Pagination";
import Toolbar from "./Toolbar";
import { dataExplorerActions } from "../../store";
import { multiSelectFilterFn } from "../../utils";
import { entities } from "../../config/config.dataExplorer";

const materialUIKey = process.env.REACT_APP_MATERIAL_UI_TOKEN;
LicenseInfo.setLicenseKey(materialUIKey);

const TitleCell = (cellValues) => {
  const selectedEntity = useSelector(
    (state) => state.dataExplorer.selectedEntity
  );
  const chipConfig = entities[selectedEntity].chipConfig;

  let title = cellValues.value;
  switch (selectedEntity) {
    case "sites":
      title =
        cellValues.row.name && cellValues.row.site_number
          ? `${cellValues.row.name} (${cellValues.row.site_number})`
          : cellValues.row.name || cellValues.row.site_number || "";
      break;
    default:
      break;
  }

  return (
    <Box key={cellValues.id}>
      <Typography sx={{ fontSize: "1rem" }}>{title}</Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
          mt: 1,
        }}
      >
        {Object.keys(chipConfig || {}).map((key, index) => {
          return cellValues.row[key] ? (
            <Box
              key={key}
              sx={{
                display: "flex",
                gap: 1,
                my: 0.5,
              }}
            >
              <Tooltip title={chipConfig[key].label}>
                {chipConfig[key].icon}
              </Tooltip>
              {[...new Set(cellValues.row[key]?.toString().split(/[,;]+/))].map(
                (chipValue, index) =>
                  chipValue ? (
                    <Chip
                      key={`${chipValue}-${index}`}
                      size="small"
                      label={chipValue}
                      sx={{
                        color: "primary.contrastText",
                        backgroundColor: chipConfig[key].color,
                      }}
                    />
                  ) : null
              )}
            </Box>
          ) : null;
        })}
      </Box>
    </Box>
  );
};

const getFilterItems = (filters) =>
  Object.keys(filters).map((filterId, index) => ({
    id: index,
    field: filterId,
    operator: "multiSelect",
    value: filters[filterId].map((v) => v.trim()),
  }));

const DataExplorer = ({ showFilters, dataName }) => {
  const dispatch = useDispatch();
  const data = useSelector((state) => state.data[dataName].data);
  const selectedEntity = useSelector(
    (state) => state.dataExplorer.selectedEntity
  );
  const filters = useSelector(
    (state) => state.dataExplorer.filters[selectedEntity]
  );
  const [filterModel, setFilterModel] = React.useState({
    items: getFilterItems(filters),
  });
  const entity = entities[selectedEntity];

  let rows = Object.values(data || {})
    .flat() // combine all arrays into single array
    .sort((d1, d2) => (d1.name > d2.name ? 1 : -1))
    .map((dataset, index) => ({
      ...dataset,
    }));

  if (entity.id === "people") {
    rows = rows.map((row) => ({
      ...row,
      name: `${row.first_name} ${row.last_name}`,
    }));
  }

  let columns = [
    {
      field: "name",
      flex: 1,
      headerName: "Name",
      renderCell: TitleCell,
      renderHeader: () => null,
    },
  ];

  // add filter options to columns
  columns = [
    ...columns,
    ...[...(entity?.filterOptions || [])].map((filter) => ({
      field: filter.id,
      headerName: filter.label,
      renderHeader: () => null,
      filterOperators: [
        {
          label: "Multi-select",
          value: "multiSelect",
          getApplyFilterFn: multiSelectFilterFn,
        },
      ],
    })),
  ];

  // hide filter columns with columnVisibilityModel
  let columnVisibilityModel = {};
  entity?.filterOptions?.forEach((filter) => {
    columnVisibilityModel[filter.id] = false;
  });

  React.useEffect(() => {
    const filterItems = getFilterItems(filters);
    setFilterModel({
      items: filterItems,
      logicOperator: GridLogicOperator.And,
    });
  }, [filters]);

  const handleCellClick = (params) => {
    dispatch(dataExplorerActions.setSelectedRow(params));
  };

  return (
    <DataGridPro
      rows={rows || []}
      columns={columns || []}
      columnVisibilityModel={columnVisibilityModel}
      filterModel={filterModel}
      // getScrollPosition={handleGetScrollPosition}
      getRowHeight={() => "auto"}
      getRowSpacing={() => 10}
      initialState={{
        pagination: { paginationModel: { pageSize: 25 } },
      }}
      onCellClick={handleCellClick}
      onFilterModelChange={(model) => setFilterModel(model)}
      pagination
      slots={{
        pagination: Pagination,
        toolbar: Toolbar,
      }}
      slotProps={{
        toolbar: {
          showFilters: showFilters,
        },
      }}
      sx={{
        border: 0,
        borderRadius: 0,
        backgroundColor: "primary.light",
        borderColor: "primary.light",

        "& .MuiDataGrid-cell:hover": {
          backgroundColor: "primary.darker",
          cursor: "pointer",
        },
        // adds padding on cells
        "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": { py: "8px" },
        "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
          py: "15px",
        },
        "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
          py: "22px",
        },
        "& .MuiDataGrid-columnHeaders": { display: "none" },
        "& .MuiDataGrid-virtualScroller": { marginTop: "0!important" },
      }}
    />
  );
};

export default DataExplorer;
